import { SalaryTrendType } from '@/lib/generated/graphql-types'

export type SalarySegment = {
  color: string
  label: string
  value?: number
}

export type TrendGroup = {
  id: string
  title: string
  trends: SalaryTrendType[]
  enabled: boolean
}

export type TrendCategory = {
  id: SalaryTrendCategory
  title: string
  groups: TrendGroup[]
  enabled: boolean
}

export const salarySegments: SalarySegment[] = [
  {
    color: 'bg-blue-60',
    label: 'Base',
  },
  {
    color: 'bg-purple-60',
    label: 'Bonus',
  },
  {
    color: 'bg-red-60',
    label: 'Other Income',
  },
]

export enum SalaryTrendCategory {
  SpecializationAndDemographic = 'specialization',
  EmployerBreakdown = 'employer',
  GeographicBreakdown = 'geographic',
}

export enum SalaryTrendGroupId {
  Specialty = 'specialty',
  Experience = 'yoe',
  EmployerType = 'employerType',
  AcademicAffiliation = 'academicAffiliation',
  TaxStatus = 'taxStatus',
  CompensationModel = 'comp_model',
  Region = 'region',
  CostOfLiving = 'costOfLiving',
  MetroType = 'metro',
}

export enum MetroValue {
  MegaMetros = 'megaMetros',
  LargeMetros = 'largeMetros',
  MetroAreas = 'metroAreas',
  SmallMetros = 'smallMetros',
  Rural = 'rural',
  SmallAndRural = 'smallMetrosAndRural',
}

export const FACTOR_FOR_MAX_TOTAL_COMPENSATION = 1.05

export const trendLabelByGroupLabel = {
  '0,2': '0-2 YOE',
  '3,5': '3-5 YOE',
  '6,10': '6-10 YOE',
  '11+': '11+ YOE',
  governmentMilitary: 'Government or Military',
  hospitalOrHealthSystem: 'Hospital or Health System',
  selfEmployed: 'Self-Employed',
  medicalGroup: 'Medical Group',
  other: 'Other',
  academic: 'Academic',
  nonAcademic: 'Non Academic',
  communityOrPublicHealthSystem: 'Community or Public Health System',
  corporatePharmaOrConsulting: 'Corporate Pharma or Consulting',
  outpatientCenter: 'Outpatient Center',
  retailClinicOrTelemedicine: 'Retail Clinic or Telemedicine',
  k1: 'K-1',
  w2: 'W-2',
  mixed: 'Mixed',
  hourly: 'Hourly + Bonus',
  partnershipBased: 'Partnership-Based',
  productivityBased: 'Productivity-Based',
  salary: 'Salary + Bonus',
  [MetroValue.MegaMetros]: 'Mega Metros',
  [MetroValue.LargeMetros]: 'Large Metros',
  [MetroValue.MetroAreas]: 'Metro Areas',
  [MetroValue.SmallMetros]: 'Small Metros',
  [MetroValue.Rural]: 'Rural',
  [MetroValue.SmallAndRural]: 'Small Metros / Rural',
}

export const trendInfoTextByGroupAndKey: Record<
  string,
  Record<string, string>
> = {
  [SalaryTrendGroupId.CompensationModel]: {
    productivityBased:
      'Compensation based primarily on productivity metrics, such as wRVUs or collections.',
    partnershipBased:
      'Compensation based on shared earnings, such as Equal Shares or Net Income.',
    other:
      'Includes Academic Hybrid models or a mix of different compensation models.',
  },
}

export const trendExtraInfoByGroupType = {
  [SalaryTrendGroupId.MetroType]: [
    {
      label: 'Mega Metros: metros with population >5M',
      enabled: MetroValue.MegaMetros,
    },
    {
      label: 'Large Metros: metros with 1-5M population',
      enabled: MetroValue.LargeMetros,
    },
    {
      label: 'Metro Areas: metros >1M population',
      enabled: MetroValue.MetroAreas,
    },
    {
      label: 'Small Metros: metros with 100k-1M population',
      enabled: MetroValue.SmallMetros,
    },
    {
      label:
        'Small Metros / Rural: metros <1M population and towns not in metro areas',
      enabled: MetroValue.SmallAndRural,
    },
    {
      label: 'Rural: towns not in metro areas',
      enabled: MetroValue.Rural,
    },
  ],
}

export const BASE_SPECIALTY_GROUP_ID = 'base_specialty_id'
export const OTHER_GROUP_ID = 'other'
